import React, {useEffect, useState} from "react";
import { useForm } from "react-hook-form";
import {useTranslation} from "react-i18next";

import TextField from '@mui/material/TextField'
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Link from "@mui/material/Link";

import {
  useResetPasswordMutation,
} from "../../../api/auth";
import { Header, Notification, NOTIFICATION_TYPE } from '@vedmarket/ui-kit';
import {ROUTES} from "../../../config/constants";
import NewPassword from "../new-password";
import {useLazyGetDictionaryLanguagesQuery} from "../../../api/localization";
import {changeAppLanguage, getLanguageCode} from "../../../config/i18";

import './../auth.scss'
import './forget-password.scss'
import {EMAIL_PATTERN} from "../../../utils/validation";

enum FORGOT_PASSWORD_FORM {
  EMAIL = 'email',
}

type ForgotPasswordForm = {
  email: string
}

const ForgetPassword = () => {

  const { t, i18n } = useTranslation()

  const [ errorEmail, setErrorEmail ] = useState('');
  const [ isSuccess, setIsSuccess ] = useState(false);

  const [ localizationGetLanguagesRequest, { data: languages } ] = useLazyGetDictionaryLanguagesQuery();

  const { register, handleSubmit, trigger, formState: { errors, isValid } } = useForm<ForgotPasswordForm>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
  })

  const selectedLanguage = languages?.find(({ code }) => getLanguageCode(code) === getLanguageCode(i18n.language))?.code;

  const [ resetPassword ] = useResetPasswordMutation()

  useEffect(() => {
    localizationGetLanguagesRequest('');
  }, [ localizationGetLanguagesRequest ]);

  useEffect(() => {
    if (errors[FORGOT_PASSWORD_FORM.EMAIL]) {
      trigger(FORGOT_PASSWORD_FORM.EMAIL);
    }
  }, [ errors, i18n.language, trigger ]);

  const onSubmit = handleSubmit (async (values) => {

    try {

      const response: any = await resetPassword({ email: values.email})

      if (response?.error) {
        throw response?.error;
      } else {
        setIsSuccess(true)
      }
    } catch (error) {
      // @ts-ignore
      setErrorEmail(error.data.email[0])
    }
  })

  return (
    <>
      <Header
        isEmpty
        languages={languages}
        user={{ lang: selectedLanguage || getLanguageCode(i18n.language) } as MeParamsOutput}
        changeLanguage={changeAppLanguage}
      />
      <form className='auth forget-password' onSubmit={onSubmit}>
        <Typography variant='h4' sx={{ fontWeight: 700, letterSpacing: '-1.15px' }}>
          {t('actions__text__passwordReset')}
        </Typography>
        <Typography variant='body1' sx={{ marginTop: 2, marginBottom: 3, maxWidth: '90%' }}>
          {t('actions__message__linkNewPassword')}
        </Typography>
        <Card sx={{
          boxShadow: 'none',
        }}>
          <CardContent className='auth__card' sx={{ padding: 3 }}>
            <div className='forget-password__form'>
              <TextField
                {...register(FORGOT_PASSWORD_FORM.EMAIL, {
                  required: t('common__text__isRequired'),
                  pattern: {
                    value: EMAIL_PATTERN,
                    message: t('errors__message__incorrectEmail')
                  }
                })}
                error={Boolean(errors[FORGOT_PASSWORD_FORM.EMAIL])}
                helperText={errors[FORGOT_PASSWORD_FORM.EMAIL]?.message}
                variant='outlined'
                label={t('common__text__email')}
              />
            </div>
            {!isSuccess && <Button
              variant='contained'
              sx={{
                marginTop: '14px',
              }}
              disabled={!isValid}
              type='submit'
            >{t('actions__message__resetLink')}</Button>}
            {errorEmail && <Notification type={NOTIFICATION_TYPE.ERROR} title={errorEmail} />}
            {isSuccess &&
                <>
                  <Typography variant='body1' sx={{ marginTop: 2, marginBottom: 2}}>
                    {t('actions__text__pleaseEnterThePasswordResetToken')}
                  </Typography>
                  <NewPassword/>
                </>}
          </CardContent>
        </Card>
        <div className='forget-password__links'>
          <Link
            href={ROUTES.LOGIN}
            sx={{
              margin: '0px 6px 0 10px'
            }}
          >{t('common__text__login')}</Link>
          <hr className='forget-password__divider' />
          <Link
            href={ROUTES.REGISTER}
            sx={{
              margin: '0px 10px 0 6px'
            }}
          >{t('common__text__registration')}</Link>
        </div>
      </form>
    </>
  )
}

export default ForgetPassword
